import { Filters } from '@rivethealth/data/advanced-filters';
import { Direction, Filter, Order } from '@rivethealth/data/super-table';
import { isEqual } from 'lodash';
import * as papaparse from 'papaparse';

/**
 * Readable string parameters
 */
export interface ReadParams {
  get(value: string): string | null;

  getAll(value: string): string[];
}

/**
 * Writable string parameters
 */
export interface WriteParams {
  set(key: string, value: string | null): void;

  setAll(key: string, value: string[]): void;
}

/**
 * Route parameter.
 */
export abstract class Param<T> {
  equals(a: T, b: T) {
    return a === b;
  }
  abstract get(params: ReadParams): T;
  abstract set(params: WriteParams, value: T): void;
}

export class NilParam extends Param<'' | null> {
  constructor(private readonly key: string) {
    super();
  }

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value != null ? <const>'' : null;
  }

  set(params: WriteParams, value: '' | null) {
    params.set(this.key, value);
  }
}

/**
 * String route parameter.
 */
export class StringParam<Value extends string> extends Param<Value> {
  constructor(
    private readonly key: string,
    private readonly default_: Value = null!,
  ) {
    super();
  }

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value != null ? <Value>value : this.default_!;
  }

  set(params: WriteParams, value: string | null) {
    params.set(this.key, value === this.default_ ? null : value);
  }
}

/**
 * String list route parameter.
 * http://base?name=a,b,c
 */
export class StringSetParam<T extends string = string> implements Param<T[]> {
  constructor(
    private readonly key: string,
    private readonly default_: T[] = [],
  ) {}

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value == null ? this.default_ : <T[]>StringSetParam.decode(value);
  }

  equals(a: T[], b: T[]) {
    return isEqual([...a].sort(), [...b].sort());
  }

  set(params: WriteParams, value: T[]) {
    params.set(
      this.key,
      this.equals(value, this.default_) ? null : StringSetParam.encode(value),
    );
  }

  static encode(values: string[]): string {
    return papaparse.unparse([values]);
  }

  private static decode(string: string): string[] {
    if (!string) {
      return [];
    }
    return papaparse.parse<string[]>(string, { delimiter: ',' }).data[0];
  }
}

/**
 * Numeric route parameter.
 */
export class NumberParam extends Param<number | null> {
  constructor(
    private readonly key: string,
    private readonly default_: number | null = null,
  ) {
    super();
  }

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value != null ? +value : this.default_;
  }

  set(params: WriteParams, value: number) {
    params.set(this.key, value === this.default_ ? null : String(value));
  }
}

/**
 * Numeric route parameter.
 */
export class RequiredNumberParam extends Param<number> {
  constructor(private readonly key: string) {
    super();
  }

  get(params: ReadParams) {
    const value = params.get(this.key);
    if (value == null) {
      throw new Error(`Missing parameter ${this.key}`);
    }
    return +value;
  }

  set(params: WriteParams, value: number) {
    params.set(this.key, String(value));
  }
}

/**
 * Numeric list route parameter.
 */
export class NumberSetParam extends Param<number[]> {
  constructor(
    private readonly key: string,
    private readonly default_: number[] = [],
  ) {
    super();
  }

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value == null ? this.default_ : NumberSetParam.decode(value);
  }

  equals(a: number[], b: number[]) {
    return isEqual([...a].sort(), [...b].sort());
  }

  set(params: WriteParams, value: number[]) {
    params.set(
      this.key,
      this.equals(value, this.default_) ? null : NumberSetParam.encode(value),
    );
  }

  private static encode(values: number[]): string {
    return papaparse.unparse([values.map(value => value.toString())]);
  }

  private static decode(string: string): number[] {
    if (!string) {
      return [];
    }
    return papaparse
      .parse<string[]>(string, { delimiter: ',' })
      .data[0].map((string: string) => +string);
  }
}

export class RangeParam extends Param<[number, number]> {
  constructor(
    private readonly key: string,
    private readonly default_: [number, number] = [0, 0],
  ) {
    super();
  }

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value == null ? this.default_ : RangeParam.decode(value);
  }

  equals(a: [number, number], b: [number, number]) {
    return a[0] === b[0] && a[1] === b[1];
  }

  set(params: WriteParams, value: [number, number]) {
    params.set(
      this.key,
      this.equals(value, this.default_) ? null : RangeParam.encode(value),
    );
  }

  private static encode(values: [number, number]): string {
    return papaparse.unparse([values.map(value => value.toString())]);
  }

  private static decode(string: string): [number, number] {
    if (!string) {
      return [0, 0];
    }
    const { data } = papaparse.parse<[string, string]>(string, {
      delimiter: ',',
    });

    return [+data[0][0], +data[0][1]];
  }
}

/**
 * Boolean route parameter.
 */
export class BooleanParam extends Param<boolean> {
  constructor(
    private readonly key: string,
    private readonly default_: boolean = false,
  ) {
    super();
  }

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value != null ? value === 'true' : this.default_;
  }

  set(params: WriteParams, value: boolean) {
    params.set(this.key, value === this.default_ ? null : String(value));
  }
}

/**
 * Multiple string filters in the router
 */
export class StringSetFilterArrayParam<T extends string = string>
  implements Param<Filter<T[]>[]>
{
  constructor(
    private readonly key: string,
    private readonly default_: Filter<T[]>[] = [],
  ) {}

  get(params: ReadParams): Filter<T[]>[] {
    const value = params.get(this.key);
    return value == null
      ? this.default_
      : StringSetFilterArrayParam.decode(value, this.default_);
  }

  equals(a: Filter<T[]>[], b: Filter<T[]>[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = StringSetFilterArrayParam.sortFilters([...a]);
    const sortedB = StringSetFilterArrayParam.sortFilters([...b]);
    return isEqual(sortedA, sortedB);
  }

  set(params: WriteParams, value: Filter<T[]>[]): void {
    params.set(
      this.key,
      this.equals(value, this.default_)
        ? null
        : StringSetFilterArrayParam.encode(value),
    );
  }

  static encode<T extends string>(filters: Filter<T[]>[]): string {
    const data = filters.map(f => Filters.multiStringFilterToString(f));
    return papaparse.unparse([data]);
  }

  private static decode<T extends string>(
    str: string,
    default_: Filter<T[]>[],
  ): Filter<T[]>[] {
    if (!str) return default_;
    const parsed = papaparse.parse<string[]>(str, { delimiter: ',' }).data[0];
    return parsed.map(row => {
      return Filters.stringToMultiStringFilter(row, default_[0]);
    });
  }

  private static sortFilters<T extends string>(
    filters: Filter<T[]>[],
  ): Filter<T[]>[] {
    return filters
      .map(f => ({
        operator: f.operator,
        filter: [...(f.filter ?? [])].sort(),
      }))
      .sort((f1, f2) => {
        // Sort by operator first then values
        if (f1.operator < f2.operator) return -1;
        if (f1.operator > f2.operator) return 1;

        // If operator is the same, sort by filter array lexicographically
        for (let i = 0; i < Math.min(f1.filter.length, f2.filter.length); i++) {
          if (f1.filter[i] < f2.filter[i]) return -1;
          if (f1.filter[i] > f2.filter[i]) return 1;
        }
        return f1.filter.length - f2.filter.length;
      });
  }
}

/**
 * String list filter route parameter.
 */
export class StringSetFilterParam<T extends string>
  implements Param<Filter<T[]>>
{
  constructor(
    private readonly key: string,
    private readonly default_: Filter<T[]> = { operator: 'NONE', filter: [] },
  ) {}

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value != null
      ? Filters.stringToMultiStringFilter(value, this.default_)
      : this.default_;
  }

  equals(a: Filter<T[]>, b: Filter<T[]>) {
    return (
      a.operator === b.operator &&
      isEqual([...a.filter!].sort(), [...b.filter!].sort())
    );
  }

  set(params: WriteParams, value: Filter<T[]>) {
    params.set(
      this.key,
      value
        ? this.equals(value, this.default_)
          ? null
          : Filters.multiStringFilterToString(value)
        : null,
    );
  }
}

export class StringFilterParam<T extends string> implements Param<Filter<T>> {
  constructor(
    private readonly key: string,
    private readonly default_: Filter<T> = { operator: 'NONE', filter: null },
  ) {}

  get(params: ReadParams) {
    const value = <T | null>params.get(this.key);
    return value != null
      ? Filters.stringToStringFilter(<T>params.get(this.key), this.default_)
      : this.default_;
  }

  equals(a: Filter<T>, b: Filter<T>) {
    return a.operator === b.operator && a.filter === b.filter;
  }

  set(params: WriteParams, value: Filter<T>) {
    params.set(
      this.key,
      value
        ? this.equals(value, this.default_)
          ? null
          : Filters.stringFilterToString(value)
        : null,
    );
  }
}

export class NumberSetFilterParam<T extends number>
  implements Param<Filter<T[]>>
{
  constructor(
    private readonly key: string,
    private readonly default_: Filter<T[]> = { operator: 'NONE', filter: [] },
  ) {}

  get(params: ReadParams) {
    const value = params.get(this.key);
    return value != null
      ? Filters.stringToMultiNumberFilter(value, this.default_)
      : this.default_;
  }

  equals(a: Filter<T[]>, b: Filter<T[]>) {
    return (
      a.operator === b.operator &&
      isEqual([...a.filter!].sort(), [...b.filter!].sort())
    );
  }

  set(params: WriteParams, value: Filter<T[]>) {
    params.set(
      this.key,
      value
        ? this.equals(value, this.default_)
          ? null
          : Filters.multiNumberFilterToString(value)
        : null,
    );
  }
}

export class ObjectParam<T> implements Param<T> {
  constructor(private readonly params: { [K in keyof T]: Param<T[K]> }) {}

  get(params: ReadParams) {
    const result: T = <any>{};
    for (const [key, param] of Object.entries(this.params)) {
      result[<keyof T>key] = (<any>param).get(params);
    }
    return result;
  }

  equals(a: T, b: T) {
    return Object.entries(this.params).every(([key, param]) =>
      (<any>param).equals(a[<keyof T>key], b[<keyof T>key]),
    );
  }

  set(params: WriteParams, value: T) {
    for (const [key, param] of Object.entries(this.params)) {
      (<Param<any>>param).set(params, value[<keyof T>key]);
    }
  }
}

export class OrderParam implements Param<Order> {
  constructor(
    private readonly default_: Order = { name: 'default', direction: 'asc' },
  ) {}

  private key = 'order';

  private stringToOrder(val: string, default_: Order): Order {
    const parts = val.split('__');
    return parts.length === 2
      ? {
          name: parts[0] || this.default_.name,
          direction: (parts[1] as Direction) || this.default_.direction,
        }
      : this.default_;
  }

  private orderToString(val: Order): string {
    return `${val.name}__${val.direction}`;
  }

  get(params: ReadParams) {
    return params.get(this.key) != null
      ? this.stringToOrder(params.get(this.key)!, this.default_)
      : this.default_;
  }

  equals(a: Order, b: Order) {
    return a.name === b.name && a.direction === b.direction;
  }

  set(params: WriteParams, value: Order) {
    params.set(
      this.key,
      this.equals(value, this.default_) ? null : this.orderToString(value),
    );
  }
}
